import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import { useApi } from 'contexts/ApiContext';

import type { api } from 'api';
import type { HTTPError } from 'api/error';
import type {
  DeleteBranchPayload,
  DeleteBranchResponse,
} from 'api/resources/draft/types';
import { curryPostFunction } from 'api/utils';
import useProjectRoute from 'hooks/useProjectRoute';
import { useProjectSettings } from 'hooks/useProjectSettings';
import { useToast } from 'hooks/useToast';
import { getBranchesQueryKey } from './useBranches';

type FunctionArgs = Omit<DeleteBranchPayload, 'accountId' | 'projectId'>;

const useDeleteDraft = (
  options?: UseMutationOptions<
    Awaited<ReturnType<typeof api.deleteStreamBranch>>,
    HTTPError,
    FunctionArgs
  >,
  overrideProjectId?: string,
) => {
  const api = useApi();
  const toast = useToast();
  const { isTemplateEngineEnabled } = useProjectSettings();

  const { accountId, projectId } = useProjectRoute();
  const projectIdToUse = overrideProjectId ?? projectId;
  const branchesQueryKey = getBranchesQueryKey(accountId, projectId);

  return useMutation<DeleteBranchResponse, HTTPError, FunctionArgs>(
    curryPostFunction(api.methods.deleteStreamBranch)({
      accountId,
      projectId: projectIdToUse,
    }),
    {
      retry: false,
      onError: () => {
        toast.error({
          title: isTemplateEngineEnabled
            ? 'Error deleting draft'
            : 'Error deleting branch',
          description: isTemplateEngineEnabled
            ? 'There was an error deleting the draft, please refresh the page and try again.'
            : 'There was an error deleting the branch, please refresh the page and try again.',
        });
      },
      meta: { invalidates: [branchesQueryKey] },
      ...options,
    },
  );
};

export default useDeleteDraft;
